export class ColourUtilities {
    // colourToRGBA uses a canvas to convert a given colour into RGBA. this is expensive, so we cache the colours.
    private static rgbaCache = new Map<string, Uint8ClampedArray>();

    /**
     * Returns the colour as an array of [r, g, b, a] -- all range from 0 - 255.
     * Colour must be a valid canvas fillStyle. This will cover almost anything
     * you'd want to use.
     * @param colour css colour string
     * @param element optional element to use as context for CSS variables
     */
    public static colourToRGBA(colour: string, element?: HTMLElement): Uint8ClampedArray {
        const resolved = ColourUtilities.resolveColour(colour, element);
        const cached = ColourUtilities.rgbaCache.get(resolved);
        if (cached) {
            return new Uint8ClampedArray(cached);
        }

        const cvs = document.createElement("canvas");
        cvs.height = 1;
        cvs.width = 1;
        const ctx = cvs.getContext("2d");
        if (ctx) {
            ctx.fillStyle = resolved;
            ctx.fillRect(0, 0, 1, 1);
            const data = ctx.getImageData(0, 0, 1, 1).data;
            ColourUtilities.rgbaCache.set(resolved, new Uint8ClampedArray(data));
            return data;
        }

        return new Uint8ClampedArray([0, 0, 0, 0]);
    }

    /**
     * Turns a number (0-255) into a 2-character hex number (00-ff)
     * @param num number to convert to hex
     */
    public static byteToHex(num: number): string {
        return ("0" + num.toString(16)).slice(-2);
    }

    /**
     * Gets the value of a CSS variable.
     * @param variableName The name of the CSS variable (e.g., '--adapt-primary')
     * @param element The element to use as context for the CSS variable.
     */
    public static getCssVariableValue(variableName: string, element?: HTMLElement): string {
        return getComputedStyle(element ?? document.documentElement).getPropertyValue(variableName).trim();
    }

    /**
     * Resolves a colour string which might be a CSS variable or a normal colour string.
     * @param colour The colour string to resolve.
     * @param element The element to use as context for the CSS variable.
     */
    public static resolveColour(colour: string, element?: HTMLElement): string {
        if (!colour) {
            return colour;
        }
        if (colour.startsWith("var(")) {
            const match = colour.match(/var\(([^)]+)\)/);
            if (match) {
                return ColourUtilities.getCssVariableValue(match[1], element);
            }
        }
        return colour;
    }

    /**
     * Returns WCAG 2.1 relative luminance for an RGBA colour array.
     * Range: 0 (black) to 1 (white).
     * @param rgba RGBA array from colourToRGBA()
     */
    public static luminance(rgba: Uint8ClampedArray | number[]): number {
        const linear = (c: number) => {
            const v = c / 255;
            return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
        };
        return 0.2126 * linear(rgba[0]) + 0.7152 * linear(rgba[1]) + 0.0722 * linear(rgba[2]);
    }

    /**
     * Blends a foreground RGBA colour over an opaque background using
     * standard alpha compositing. Returns an opaque Uint8ClampedArray.
     * @param fg foreground colour with alpha
     * @param bg background colour (opaque)
     */
    public static blendOver(fg: Uint8ClampedArray | number[], bg: Uint8ClampedArray | number[]): Uint8ClampedArray {
        const a = fg[3] / 255;
        return new Uint8ClampedArray([
            Math.round(fg[0] * a + bg[0] * (1 - a)),
            Math.round(fg[1] * a + bg[1] * (1 - a)),
            Math.round(fg[2] * a + bg[2] * (1 - a)),
            255,
        ]);
    }

    /**
     * Picks the light or dark colour for best contrast on a background with
     * the given WCAG luminance. Defaults to --bs-body-color / --bs-white.
     * @param luminance WCAG relative luminance of the background (0–1)
     * @param darkColour dark text colour (default: var(--bs-body-color))
     * @param lightColour light text colour (default: var(--bs-white))
     */
    public static contrastTextColour(
        luminance: number,
        darkColour = "var(--bs-body-color)",
        lightColour = "var(--bs-white)",
    ): string {
        // --bs-body-color is #343a40, WCAG luminance ~0.041; --bs-white = 1.0
        const darkContrast = (luminance + 0.05) / 0.091;
        const lightContrast = 1.05 / (luminance + 0.05);
        return darkContrast > lightContrast * 1.5 ? darkColour : lightColour;
    }
}
